import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createEntry, getProfile, getEntryById, updateEntry, getSubscriptionStatus, getAllEntriesByUsername } from '../services/api';
import { SkeletonFormCard } from './SkeletonCard';
import './AddEntry.css';

function AddEntry() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const loggedInUsername = localStorage.getItem('username');

  const [profile, setProfile] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [monthlyUsage, setMonthlyUsage] = useState(null); // { used, limit, partnerName }
  const [formData, setFormData] = useState({
    username: loggedInUsername,
    partnerName: '',
    category: '',
    description: '',
    rating: 5
  });
  const [saveAsPrivate, setSaveAsPrivate] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingEntry, setLoadingEntry] = useState(isEditMode);

  const categories = [
    'Emotional Intimacy', 'Sexual Satisfaction', 'Communication Quality',
    'Conflict Resolution', 'Trust & Honesty', 'Quality Time',
    'Shared Goals', 'Affection & Romance', 'Financial Harmony', 'Personal Growth Support'
  ];

  const categoryDefinitions = {
    'Emotional Intimacy': 'The ability to share feelings, vulnerabilities, and deep emotions with your partner. This includes feeling understood, supported, and emotionally connected.',
    'Sexual Satisfaction': 'The level of fulfillment and connection in your physical intimate relationship. This encompasses desire, satisfaction, and mutual comfort.',
    'Communication Quality': 'How effectively you and your partner express thoughts, listen actively, and understand each other. This includes verbal and non-verbal communication.',
    'Conflict Resolution': 'Your ability to handle disagreements constructively, find compromises, and resolve issues without damaging the relationship.',
    'Trust & Honesty': 'The foundation of reliability, transparency, and truthfulness in your relationship. This includes keeping promises and being authentic.',
    'Quality Time': 'The meaningful time spent together, giving each other undivided attention and creating shared experiences and memories.',
    'Shared Goals': 'Alignment on future plans, values, and life direction. This includes working together toward common objectives and dreams.',
    'Affection & Romance': 'Physical and emotional expressions of love, appreciation, and attraction. This includes gestures, words, and actions that show care.',
    'Financial Harmony': 'Agreement and cooperation in managing money, financial goals, spending habits, and economic responsibilities.',
    'Personal Growth Support': "How well you and your partner encourage each other's individual development, hobbies, career goals, and personal aspirations."
  };

  useEffect(() => {
    if (!loggedInUsername) { navigate('/login'); return; }
    fetchProfile();
    fetchSubscription();
    if (isEditMode) fetchEntry();
  }, [loggedInUsername, navigate, isEditMode, id]);

  const fetchSubscription = async () => {
    try {
      const sub = await getSubscriptionStatus(loggedInUsername);
      setIsPremium(sub.isPremium);
    } catch (err) {
      console.error('Error fetching subscription:', err);
    }
  };

  const fetchMonthlyUsage = async (partnerName) => {
    try {
      const data = await getAllEntriesByUsername(loggedInUsername);
      const now = new Date();
      const count = data.filter(entry => {
        if (entry.partnerName !== partnerName) return false;
        const entryDate = new Date(entry.date);
        return entryDate.getFullYear() === now.getFullYear() && entryDate.getMonth() === now.getMonth();
      }).length;
      setMonthlyUsage({ used: count, limit: 8, partnerName });
    } catch (err) {
      console.error('Error fetching monthly usage:', err);
    }
  };

  const fetchProfile = async () => {
    try {
      setLoadingProfile(true);
      const profileData = await getProfile(loggedInUsername);
      if (profileData.exists !== false && profileData.maleName && profileData.femaleName && profileData.maleAge && profileData.femaleAge) {
        setProfile(profileData);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoadingProfile(false);
    }
  };

  const fetchEntry = async () => {
    try {
      setLoadingEntry(true);
      const entry = await getEntryById(id, loggedInUsername);
      setFormData({
        username: entry.username,
        partnerName: entry.partnerName,
        category: entry.category,
        description: entry.description || '',
        rating: entry.rating
      });
      setSaveAsPrivate(entry.isPrivate || false);
    } catch (error) {
      console.error('Error fetching entry:', error);
      setErrorMessage('Failed to load entry. Redirecting...');
      setTimeout(() => navigate('/view-entries'), 2000);
    } finally {
      setLoadingEntry(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setSuccessMessage('');
    setErrorMessage('');
    if (e.target.name === 'partnerName' && e.target.value && !isPremium) {
      fetchMonthlyUsage(e.target.value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');

    if (!formData.partnerName) { setErrorMessage('Please select who is making this entry'); return; }
    if (!formData.category) { setErrorMessage('Please select a category'); return; }

    setLoading(true);

    const entry = {
      username: loggedInUsername,
      partnerName: formData.partnerName,
      category: formData.category,
      rating: parseInt(formData.rating),
      description: formData.description,
      date: new Date().toISOString().split('T')[0],
      isPrivate: saveAsPrivate,
      visibility: 'both',
      pinProtected: saveAsPrivate
    };

    try {
      if (isEditMode) {
        await updateEntry(id, entry, loggedInUsername);
        setSuccessMessage('✅ Entry updated successfully!');
      } else {
        await createEntry(entry);
        setSuccessMessage(saveAsPrivate ? '🔒 Private entry added successfully!' : 'Entry added successfully! ✨');
      }

      setTimeout(() => {
        setSuccessMessage('');
        navigate('/view-entries');
      }, 2000);
    } catch (error) {
      const errorData = error.response?.data;
      if (errorData?.error === 'ENTRY_LIMIT_REACHED') {
        setErrorMessage('⭐ ' + errorData.message + ' Redirecting...');
        setTimeout(() => navigate('/plus'), 3000);
      } else if (errorData?.error === 'PREMIUM_REQUIRED') {
        setErrorMessage('🔒 Private entries are a Premium feature. Upgrade to Premium to unlock. Redirecting...');
        setTimeout(() => navigate('/plus'), 3000);
      } else {
        setErrorMessage(isEditMode ? 'Failed to update entry. Please try again.' : 'Failed to add entry. Please try again.');
      }
      console.error(isEditMode ? 'Error updating entry:' : 'Error creating entry:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRatingColor = (rating) => {
    if (rating >= 8) return '#4caf50';
    if (rating >= 5) return '#ffd93d';
    return '#ff6b6b';
  };

  const getRatingLabel = (rating) => {
    if (rating >= 8) return 'Excellent';
    if (rating >= 5) return 'Good';
    return 'Needs Work';
  };

  const getNameOptions = () => {
    if (loadingProfile) return [<option key="loading" value="">Loading names...</option>];
    if (!profile || !profile.maleName || !profile.femaleName || !profile.maleAge || !profile.femaleAge) {
      return [<option key="none" value="">Complete your profile first</option>];
    }
    return [
      <option key="male" value={profile.maleName}>{profile.maleName} (Male Partner)</option>,
      <option key="female" value={profile.femaleName}>{profile.femaleName} (Female Partner)</option>
    ];
  };

  if (loadingEntry) {
    return (
      <div className="add-entry-container">
        <div className="add-entry-header">
          <h1>✏️ Edit Entry</h1>
          <p>Loading your entry...</p>
        </div>
        <SkeletonFormCard />
      </div>
    );
  }

  return (
    <div className="add-entry-container">
      <div className="add-entry-header">
        <h1>{isEditMode ? '✏️ Edit Entry' : '📝 Add New Entry'}</h1>
        <p>{isEditMode ? 'Update your relationship experience' : 'Record your relationship experience today'}</p>
      </div>

      {successMessage && <div className="success-message">{successMessage}</div>}

      {errorMessage && (
        <div className="error-message">
          {errorMessage}
          {(errorMessage.includes('limit') || errorMessage.includes('Premium')) && (
            <button className="upgrade-prompt-btn" onClick={() => navigate('/plus')}>
              ⭐ Upgrade to Premium
            </button>
          )}
        </div>
      )}

      {!profile || !profile.maleName || !profile.femaleName || !profile.maleAge || !profile.femaleAge ? (
        <div className="profile-warning">
          <div className="warning-icon">⚠️</div>
          <h3>Profile Incomplete</h3>
          <p>Please complete your profile with both partner names and ages before adding entries.</p>
          <button className="complete-profile-button" onClick={() => navigate('/profile')}>Go to Profile</button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="entry-form">
          <div className="form-group">
            <label htmlFor="partnerName">Who's Making This Entry? *</label>
            <select id="partnerName" name="partnerName" value={formData.partnerName} onChange={handleChange} required disabled={loading || loadingProfile}>
              <option value="">Select name...</option>
              {getNameOptions()}
            </select>
            {!isPremium && monthlyUsage && monthlyUsage.partnerName === formData.partnerName && (
              <div className={`entry-usage-indicator ${monthlyUsage.used >= monthlyUsage.limit ? 'at-limit' : monthlyUsage.used >= 6 ? 'near-limit' : ''}`}>
                <span className="usage-icon">📊</span>
                <span className="usage-text">
                  {monthlyUsage.partnerName}: <strong>{monthlyUsage.used}/{monthlyUsage.limit}</strong> entries used this month
                </span>
                {monthlyUsage.used >= monthlyUsage.limit && (
                  <button type="button" className="usage-upgrade-btn" onClick={() => navigate('/plus')}>⭐ Upgrade</button>
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="category">Checkpoint *</label>
            <select id="category" name="category" value={formData.category} onChange={handleChange} required disabled={loading}>
              <option value="">Select a Checkpoint...</option>
              {categories.map((cat) => <option key={cat} value={cat}>{cat}</option>)}
            </select>
            {formData.category && (
              <div className="category-definition">
                <div className="definition-icon">💡</div>
                <p>{categoryDefinitions[formData.category]}</p>
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="rating">
              Rating: {formData.rating}/10
              <span className="rating-label" style={{ color: getRatingColor(formData.rating) }}>
                ({getRatingLabel(formData.rating)})
              </span>
            </label>
            <input
              type="range" id="rating" name="rating"
              min="1" max="10" value={formData.rating}
              onChange={handleChange} disabled={loading}
              style={{ background: `linear-gradient(to right, #ff6b6b 0%, #ffd93d 50%, #4caf50 100%)` }}
            />
            <div className="rating-scale">
              <span>1 (Poor)</span>
              <span>5 (Average)</span>
              <span>10 (Excellent)</span>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">Description (Optional)</label>
            <textarea
              id="description" name="description" value={formData.description}
              onChange={handleChange}
              placeholder="Share details about today's experience in this area. What went well? What could improve?"
              rows="5" maxLength="1000" disabled={loading}
            />
            <small>{formData.description.length}/1000 characters</small>
          </div>

          <div className="privacy-toggle-section">
            <label className="privacy-toggle-label">
              <input
                type="checkbox" checked={saveAsPrivate}
                onChange={(e) => setSaveAsPrivate(e.target.checked)}
                className="privacy-checkbox"
              />
              <span className="privacy-toggle-text">
                <span className="privacy-icon">{saveAsPrivate ? '🔒' : '🔓'}</span>
                Save as Private Entry
                {saveAsPrivate && <span className="privacy-badge">PIN Protected</span>}
              </span>
            </label>
            {saveAsPrivate && <p className="privacy-hint">💡 This entry will be private and require your PIN to view</p>}
          </div>

          <button type="submit" className="submit-button" disabled={loading}>
            {loading
              ? (isEditMode ? 'Updating Entry...' : 'Adding Entry...')
              : isEditMode
                ? '💾 Save Changes'
                : (saveAsPrivate ? '🔒 Add Private Entry' : '✨ Add Entry')}
          </button>

          {isEditMode && (
            <button type="button" className="cancel-button" onClick={() => navigate('/view-entries')} disabled={loading}>
              Cancel
            </button>
          )}
        </form>
      )}

      <div className="tips-section">
        <h3>💡 Tips for Meaningful Entries</h3>
        <ul>
          <li>Be honest and authentic in your ratings</li>
          <li>Both partners should make entries regularly</li>
          <li>Use this as a tool for growth, not criticism</li>
          <li>Review your entries together weekly</li>
          <li>Track patterns over time for insights</li>
        </ul>
      </div>
    </div>
  );
}

export default AddEntry;